import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";
import StockIndentService from "../services/stockIndentService";
import ItemsService from "../services/ItemsServices";
import ProjectService from "../services/ProjectService";
import StorageService from "../services/StorageService";
import Loading from "../components/loading";
import usePermission from "../utils/UsePermission";
import ScreenData from "../config/ScreenData";
import UnAuthorized from "../views/Unathorized";
import ReactPaginate from "react-paginate";
import MessageDialog from "../components/MessageDialog";
import utility from "../utils/utility";
import { useReactToPrint } from "react-to-print";
import logo from "../assets/images/deviops_logo.png";

export default function ViewStockIndent() {
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [message, setMessage] = useState("");
  const [header, setHeader] = useState(null);
  const [msgType, setMsgType] = useState("");
  const [loading, setLoading] = useState(true);
  const companyId = StorageService.activeCompany()?.companyId;
  const employeeId = StorageService.loginData()?.employeeId;
  const roleId = StorageService.loginData()?.roleId;

  const [popup, setPopup] = useState(false);
  const [detailRows, setDetailRows] = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);

  // APPROVE MODAL
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveItem, setApproveItem] = useState(null);
  const [approveNote, setApproveNote] = useState("");

  // REJECT MODAL
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectNote, setRejectNote] = useState("");

  // CANCEL MODAL
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelItem, setCancelItem] = useState(null);

  const [itemMap, setItemMap] = useState({});
  const [projectList, setProjectList] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [allRows, setAllRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const itemsPerPage = 10;

  const navigate = useNavigate();

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const { permission, can } = usePermission(
    ScreenData.Stock_Indent,
    showMessage
  );

  const companyName = StorageService.activeCompany()?.companyName || "Devi Projects";
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Stock_Indent_${header?.vocherNo || ""}`,
  });

  const formatEmployee = (name, code) => {
    if (name && code) return `${name} (${code})`;
    return name || code || "-";
  };

  const formatDateOnly = (date) =>
    date ? utility.formatDateDDMMMYYYY(date) : "-";

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setCurrentPage(1);
      setSearch(searchText);
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const pRes = await ProjectService.getActive(companyId);
        const pData = pRes?.data || pRes || [];
        setProjectList(Array.isArray(pData) ? pData : []);
      } catch (e) {
        console.error("Failed to load projects:", e);
      }
    };
    if (companyId) {
      fetchProjects();
    }
  }, [companyId]);

  const formatProjectDisplay = (project) => {
    if (!project) return "-";
    let projObj = project;
    if (typeof project === "string" || typeof project === "number") {
      const match =
        projectList.find(
          (p) =>
            String(p.id) === String(project) ||
            String(p.projectId) === String(project) ||
            p.projectName === project
        ) || {};
      projObj = Object.keys(match).length ? match : { projectName: String(project) };
    }

    const projectId = projObj.projectId || projObj.id;
    const matchedProject =
      (projectId &&
        projectList.find(
          (p) => String(p.id) === String(projectId) || String(p.projectId) === String(projectId)
        )) ||
      (projObj.projectName &&
        projectList.find((p) => p.projectName === projObj.projectName)) ||
      {};

    const projectName =
      projObj.projectName || projObj.name || matchedProject.projectName || "";
    const categoryName =
      projObj.projectCategoryName ||
      projObj.categoryName ||
      projObj.category ||
      matchedProject.projectCategoryName ||
      matchedProject.categoryName ||
      matchedProject.category ||
      "";
    const projectCode =
      projObj.projectCode || matchedProject.projectCode || "";
    const categoryInitial = String(categoryName).trim().charAt(0).toUpperCase();

    if (projectName) {
      if (categoryInitial) {
        return `${categoryInitial}*${projectName}`;
      }
      if (projectCode) {
        const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
        return `${codeInitial}*${projectName}`;
      }
      return projectName;
    }
    if (projectCode) {
      const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
      return `${codeInitial}*`;
    }
    return categoryInitial ? `${categoryInitial}*` : "-";
  };

  useEffect(() => {
    if (permission === null) return;

    if (permission.canView !== 1) {
      setLoading(false);
      return;
    }

    loadAll();
  }, [companyId, employeeId, roleId, currentPage, permission, search, statusFilter]);

  useEffect(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setRows(allRows.slice(start, end));
  }, [allRows, currentPage]);

  const loadAll = async () => {
    try {
      setLoading(true);

      const res = await StockIndentService.getAll(
        companyId,
        employeeId,
        roleId,
        1,
        1000,
        search,
        statusFilter
      );

      const data = res?.data?.data || [];
      const sortedData = [...data].sort((a, b) => b.id - a.id);

      setAllRows(sortedData);
      const totalPages = Math.max(1, Math.ceil(sortedData.length / itemsPerPage));
      setPageCount(totalPages);
      const safePage = Math.min(currentPage, totalPages);
      if (safePage !== currentPage) {
        setCurrentPage(safePage);
      }
      const start = (safePage - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      setRows(sortedData.slice(start, end));
      setSelectedId(null);
    } catch (err) {
      console.error("GET ALL ERROR:", err);
      showMessage(err.message || "Error fetching stock indents.");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate("/cpnl/StockIndent/add");
  };

  const handleModify = () => {
    if (!selectedId) {
      return showMessage("Please select one record to modify.");
    }

    const selectedRecord =
      rows.find((r) => r.id === selectedId) ||
      allRows.find((r) => r.id === selectedId);

    if (!selectedRecord) {
      return showMessage("Selected record not found.");
    }

    if (selectedRecord.status && selectedRecord.status.toUpperCase() !== "PENDING") {
      return showMessage("Only PENDING records can be modified.");
    }

    navigate(`/cpnl/StockIndent/add/${selectedId}`);
  };

  const handleView = () => {
    if (!selectedId) {
      return showMessage("Please select one stock indent to view.");
    }
    loadPopup(selectedId);
  };

  const submitApprove = async () => {
    if (!approveNote.trim()) {
      showMessage("Please enter approval note");
      return;
    }

    try {
      await StockIndentService.approve(approveItem.id, {
        approvedBy: employeeId,
        approvedNotes: approveNote,
      });

      setRows((prev) =>
        prev.map((r) =>
          r.id === approveItem.id ? { ...r, status: "APPROVED" } : r
        )
      );
      setAllRows((prev) =>
        prev.map((r) =>
          r.id === approveItem.id ? { ...r, status: "APPROVED" } : r
        )
      );

      showMessage("Stock Indent approved successfully", "success");
      setShowApproveModal(false);
    } catch (err) {
      console.error(err);
      showMessage(err.response?.data?.message || "Approval failed");
    }
  };

  const submitReject = async () => {
    if (!rejectNote.trim()) {
      showMessage("Please enter rejection note");
      return;
    }

    try {
      await StockIndentService.reject(rejectItem.id, {
        rejectedBy: employeeId,
        rejectedNotes: rejectNote,
      });

      setRows((prev) =>
        prev.map((r) =>
          r.id === rejectItem.id ? { ...r, status: "REJECTED" } : r
        )
      );
      setAllRows((prev) =>
        prev.map((r) =>
          r.id === rejectItem.id ? { ...r, status: "REJECTED" } : r
        )
      );

      showMessage("Stock Indent rejected successfully", "success");
      setShowRejectModal(false);
    } catch (err) {
      console.error(err);
      showMessage(err.response?.data?.message || "Rejection failed");
    }
  };

  const submitCancel = async () => {
    try {
      await StockIndentService.cancel(cancelItem.id, {
        cancelledBy: employeeId,
      });

      setRows((prev) =>
        prev.map((r) =>
          r.id === cancelItem.id ? { ...r, status: "CANCELLED" } : r
        )
      );
      setAllRows((prev) =>
        prev.map((r) =>
          r.id === cancelItem.id ? { ...r, status: "CANCELLED" } : r
        )
      );

      showMessage("Stock Indent cancelled successfully", "success");
      setShowCancelModal(false);
    } catch (err) {
      console.error(err);
      showMessage(err.response?.data?.message || "Failed to cancel Stock Indent", "error");
    }
  };

  const loadPopup = async (id) => {
    try {
      setPopup(true);
      setPopupLoading(true);

      let localItemMap = { ...itemMap };
      if (!Object.keys(localItemMap).length) {
        const itemRes = await ItemsService.getAllItems();
        const iMap = {};
        (itemRes?.data || itemRes || []).forEach((i) => {
          const key = String(i.id ?? i.itemId ?? i.item_id ?? "");
          iMap[key] = {
            code: i.itemCode ?? "",
            categoryName: i.itemCategoryName ?? i.categoryName ?? i.itemCategory ?? "",
            uomName: i.uomName ?? i.uomCode ?? i.uom ?? i.UOM ?? "",
          };
        });
        setItemMap(iMap);
        localItemMap = iMap;
      }

      const res = await StockIndentService.getById(id);
      const responseData = res?.data?.data ?? {};

      const selectedRecord =
        rows.find((r) => r.id === id) ||
        allRows.find((r) => r.id === id) ||
        {};
      const fetchedHeader =
        responseData.stockIndent ||
        (responseData.id ? responseData : {});
      const headerData = {
        ...selectedRecord,
        ...fetchedHeader,
        projectName: fetchedHeader.projectName || selectedRecord.projectName || "",
        projectCode: fetchedHeader.projectCode || selectedRecord.projectCode || "",
        projectCategoryName:
          fetchedHeader.projectCategoryName ||
          selectedRecord.projectCategoryName ||
          "",
        projectCategoryCode:
          fetchedHeader.projectCategoryCode ||
          selectedRecord.projectCategoryCode ||
          "",
      };
      setHeader(headerData);

      const details =
        headerData.details?.length
          ? headerData.details
          : responseData.details || [];

      const mapped = details.map((d) => {
        const itemIdKey = String(d.itemId ?? "");
        const item = localItemMap[itemIdKey] || {};
        const code = d.itemCode || item.code || (typeof item === "string" ? item : "-");
        const uom = (d.UOM && d.UOM !== "-") ? d.UOM : (d.uom || d.uomName || item.uomName || "-");
        return {
          itemId: d.itemId,
          itemCode: code,
          itemCategoryName: d.itemCategoryName || d.categoryName || d.itemCategory || item.categoryName || "-",
          needByDate: d.needByDate,
          UOM: uom,
          quantity: d.quantity ?? 0,
        };
      });

      setDetailRows(mapped);
    } catch (err) {
      console.error("POPUP LOAD ERROR:", err);
      showMessage("Error loading details.");
      setDetailRows([]);
    } finally {
      setPopupLoading(false);
    }
  };

  if (permission === null) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (!permission || permission.canView !== 1) {
    return <UnAuthorized />;
  }

  return (
    <>
      <Breadcrumb pageTitle="Stock Indent" secondTitle="Inventory" />

      <div className="card p-3 bg-white shadow-sm">
        <MessageDialog
          message={message}
          type={msgType}
          setMessage={setMessage}
          setMsgType={setMsgType}
        />

        <div className="mb-3 pb-2 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center flex-wrap gap-2">
            {can("canAdd") && (
              <button
                className="btn btn-success me-2"
                onClick={handleAdd}
                disabled={loading}
              >
                <i className="ti ti-plus me-1"></i> Add
              </button>
            )}

            {can("canModify") && (
              <button
                className="btn btn-warning me-2"
                onClick={handleModify}
                disabled={
                  loading ||
                  !selectedId ||
                  rows.find((r) => r.id === selectedId)?.status?.toUpperCase() !== "PENDING"
                }
              >
                <i className="ti ti-edit me-1"></i> Modify
              </button>
            )}

            {can("canView") && (
              <button
                className="btn btn-primary me-2"
                onClick={handleView}
                disabled={loading}
              >
                <i className="ti ti-eye me-1"></i> View
              </button>
            )}

            <select
              className="form-select d-inline w-auto"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <div className="d-flex align-items-center ms-auto">
            <div className="position-relative">
              <input
                type="text"
                className="form-control pe-5"
                placeholder="Search Project / Indent RequestNo"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: "280px" }}
              />
              <i
                className="ti ti-search position-absolute"
                style={{
                  right: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#6c757d",
                }}
              ></i>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-striped mb-0">
            <thead className="table-light">
              <tr>
                <th>Select</th>
                <th>Indent RequestNo</th>
                <th>Indent Date</th>
                <th>Project</th>
                <th>Store</th>
                <th>Location</th>
                <th>Notes</th>
                <th>Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    <Loading />
                  </td>
                </tr>
              ) : rows.length > 0 ? (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <input
                        type="radio"
                        name="stockIndentSelect"
                        checked={selectedId === row.id}
                        onChange={() => setSelectedId(row.id)}
                      />
                    </td>
                    <td>{row.vocherNo || "-"}</td>
                    <td>{formatDateOnly(row.indentDate)}</td>
                    <td>{formatProjectDisplay(row)}</td>
                    <td>{row.storeName || "-"}</td>
                    <td>{row.locationName || "-"}</td>
                    <td>{row.stockIndentNotes || "-"}</td>
                    <td>
                      <span
                        className={`badge ${
                          row.status === "PENDING"
                            ? "bg-info text-dark"
                            : row.status === "APPROVED"
                            ? "bg-success"
                            : row.status === "REJECTED"
                            ? "bg-danger"
                            : row.status === "CANCELLED"
                            ? "bg-dark"
                            : "bg-secondary"
                        }`}
                      >
                        {row.status || "PENDING"}
                      </span>
                    </td>
                    <td className="text-center d-flex justify-content-center gap-2">
                      {can("canApprove") && (
                        <button
                          className="btn btn-success btn-sm"
                          title="Approve"
                          disabled={row.status?.toUpperCase() !== "PENDING"}
                          onClick={() => {
                            setApproveItem(row);
                            setApproveNote("");
                            setShowApproveModal(true);
                          }}
                        >
                          <i className="ti ti-circle-check"></i>
                        </button>
                      )}

                      {can("canReject") && (
                        <button
                          className="btn btn-danger btn-sm"
                          title="Reject"
                          disabled={row.status?.toUpperCase() !== "PENDING"}
                          onClick={() => {
                            setRejectItem(row);
                            setRejectNote("");
                            setShowRejectModal(true);
                          }}
                        >
                          <i className="ti ti-circle-x"></i>
                        </button>
                      )}

                      {can("canCancel") && (
                        <button
                          className="btn btn-secondary btn-sm"
                          title="Cancel"
                          disabled={
                            row.status?.toUpperCase() !== "PENDING" ||
                            Number(row.createdBy) !== Number(employeeId)
                          }
                          onClick={() => {
                            setCancelItem(row);
                            setShowCancelModal(true);
                          }}
                        >
                          <i className="ti ti-cancel"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center text-muted">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <ReactPaginate
            forcePage={Math.max(0, Math.min(currentPage - 1, Math.max(pageCount - 1, 0)))}
            onPageChange={(e) => setCurrentPage(e.selected + 1)}
            pageCount={Math.max(1, pageCount)}
            pageRangeDisplayed={3}
            marginPagesDisplayed={2}
            previousLabel="< Previous"
            nextLabel="Next >"
            containerClassName="pagination-container"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            nextClassName="page-item"
            activeClassName="active"
          />
        </div>
      </div>

      {/* Popup Modal for Details */}
      {popup && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
          onClick={() => setPopup(false)}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Stock Indent Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setPopup(false)}
                ></button>
              </div>

              <div className="modal-body">
                {popupLoading ? (
                  <div className="text-center p-4">
                    <Loading />
                  </div>
                ) : (
                  <>
                    {header && (
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <strong>Indent RequestNo:</strong>{" "}
                          <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>
                            {header.vocherNo || "-"}
                          </span>
                        </div>
                        <div className="col-md-6">
                          <strong>Indent Date:</strong>{" "}
                          <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>
                            {formatDateOnly(header.indentDate)}
                          </span>
                        </div>

                        <div className="col-md-6 mt-2">
                          <strong>Project Name:</strong>{" "}
                          <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>
                            {formatProjectDisplay(header)}
                          </span>
                        </div>
                        <div className="col-md-6 mt-2">
                          <strong>Store Name:</strong>{" "}
                          <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>
                            {header.storeName || "-"}
                          </span>
                        </div>

                        <div className="col-md-6 mt-2">
                          <strong>Location Name:</strong>{" "}
                          <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>
                            {header.locationName || "-"}
                          </span>
                        </div>
                        <div className="col-md-6 mt-2">
                          <strong>Status:</strong>{" "}
                          <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>
                            {header.status || "PENDING"}
                          </span>
                        </div>

                        <div className="col-md-6 mt-2">
                          <strong>Created By:</strong>{" "}
                          {formatEmployee(header.createdByName, header.createdByCode)}
                        </div>
                        <div className="col-md-6 mt-2">
                          <strong>Notes:</strong>{" "}
                          <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>
                            {header.stockIndentNotes || "-"}
                          </span>
                        </div>

                        {header.status?.toUpperCase() === "APPROVED" && (
                          <>
                            <div className="col-md-6 mt-2">
                              <strong>Approved By:</strong> {formatEmployee(header.approvedByName, header.approvedByCode)}
                            </div>
                            <div className="col-md-6 mt-2">
                              <strong>Approved Notes:</strong> {header.approvedNotes || "-"}
                            </div>
                          </>
                        )}

                        {header.status?.toUpperCase() === "REJECTED" && (
                          <>
                            <div className="col-md-6 mt-2">
                              <strong>Rejected By:</strong> {formatEmployee(header.rejectedByName, header.rejectedByCode)}
                            </div>
                            <div className="col-md-6 mt-2">
                              <strong>Rejected Notes:</strong> {header.rejectedNotes || "-"}
                            </div>
                          </>
                        )}

                        {header.status?.toUpperCase() === "CANCELLED" && (
                          <>
                            <div className="col-md-6 mt-2">
                              <strong>Cancelled By:</strong> {formatEmployee(header.cancelledByName, header.cancelledByCode)}
                            </div>
                            <div className="col-md-6 mt-2">
                              <strong>Cancelled Notes:</strong> {header.cancelledNotes || "-"}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <table className="table table-bordered table-striped">
                      <thead className="table-light">
                        <tr>
                          <th>Item Category</th>
                          <th>Item</th>
                          <th>Need By Date</th>
                          <th>UOM</th>
                          <th className="text-end">Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailRows.length > 0 ? (
                          detailRows.map((d, i) => (
                            <tr key={i}>
                              <td>{d.itemCategoryName || "-"}</td>
                              <td>{d.itemCode}</td>
                              <td>{formatDateOnly(d.needByDate)}</td>
                              <td>{d.UOM}</td>
                              <td className="text-end font-weight-bold">
                                {Number(d.quantity || 0).toFixed(3)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center">
                              No records found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* PRINT TEMPLATE */}
                    <div style={{ display: "none" }}>
                      <div ref={printRef} className="print-container">
                        <div className="print-top-header">
                          <div className="print-top-left">
                            <img src={logo} alt="Logo" className="print-logo" />
                            <span className="print-company">{companyName}</span>
                          </div>
                          <div className="print-top-right">
                            <b>Printed Date:</b> {utility.formatExportDateTime(new Date())}
                          </div>
                        </div>

                        <h3 className="print-title">Stock Indent Slip</h3>

                        <table className="print-header">
                          <tbody>
                            <tr>
                              <td><b>Indent No:</b> {header?.vocherNo || "-"}</td>
                              <td><b>Indent Date:</b> {formatDateOnly(header?.indentDate)}</td>
                            </tr>
                            <tr className="project-store-row">
                              <td><b>Project Name:</b> {formatProjectDisplay(header)}</td>
                              <td><b>Store Name:</b> {header?.storeName || "-"}</td>
                            </tr>
                            <tr>
                              <td><b>Location Name:</b> {header?.locationName || "-"}</td>
                              <td><b>Status:</b> {header?.status || "PENDING"}</td>
                            </tr>
                            <tr>
                              <td><b>Created By:</b> {formatEmployee(header?.createdByName, header?.createdByCode)}</td>
                              <td><b>Notes:</b> {header?.stockIndentNotes || "-"}</td>
                            </tr>
                            {header?.status?.toUpperCase() === "APPROVED" && (
                              <tr>
                                <td><b>Approved By:</b> {formatEmployee(header?.approvedByName, header?.approvedByCode)}</td>
                                <td><b>Approved Notes:</b> {header?.approvedNotes || "-"}</td>
                              </tr>
                            )}
                            {header?.status?.toUpperCase() === "REJECTED" && (
                              <tr>
                                <td><b>Rejected By:</b> {formatEmployee(header?.rejectedByName, header?.rejectedByCode)}</td>
                                <td><b>Rejected Notes:</b> {header?.rejectedNotes || "-"}</td>
                              </tr>
                            )}
                            {header?.status?.toUpperCase() === "CANCELLED" && (
                              <tr>
                                <td><b>Cancelled By:</b> {formatEmployee(header?.cancelledByName, header?.cancelledByCode)}</td>
                                <td><b>Cancelled Notes:</b> {header?.cancelledNotes || "-"}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        <table className="print-table">
                          <thead>
                            <tr>
                              <th className="text-center">S.No</th>
                              <th className="text-start">Item Category</th>
                              <th className="text-start">Item Code</th>
                              <th className="text-center">Need By Date</th>
                              <th className="text-start">UOM</th>
                              <th className="text-end">Quantity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailRows.map((d, i) => (
                              <tr key={i}>
                                <td className="text-center">{i + 1}</td>
                                <td className="text-start">{d.itemCategoryName || "-"}</td>
                                <td className="text-start">{d.itemCode}</td>
                                <td className="text-center">{formatDateOnly(d.needByDate)}</td>
                                <td className="text-start">{d.UOM}</td>
                                <td className="text-end">{Number(d.quantity || 0).toFixed(3)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <div style={{ marginTop: "60px", display: "flex", justifyContent: "space-between" }}>
                          <div>Requested By Signature</div>
                          <div>Store Incharge Signature</div>
                          <div>Approved By Signature</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-success me-2"
                  onClick={handlePrint}
                >
                  <i className="ti ti-printer me-1"></i> Print
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPopup(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* APPROVE MODAL */}
      {showApproveModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Approve Stock Indent</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowApproveModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to approve this Stock Indent request?</p>
                <label className="form-label">
                  Approval Note <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={approveNote}
                  onChange={(e) => setApproveNote(e.target.value)}
                  placeholder="Enter approval note..."
                ></textarea>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowApproveModal(false)}
                >
                  Close
                </button>
                <button className="btn btn-success" onClick={submitApprove}>
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {showRejectModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reject Stock Indent</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowRejectModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to reject this Stock Indent request?</p>
                <label className="form-label">
                  Rejection Note <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  placeholder="Enter rejection note..."
                ></textarea>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowRejectModal(false)}
                >
                  Close
                </button>
                <button className="btn btn-danger" onClick={submitReject}>
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CANCEL MODAL */}
      {showCancelModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Cancel Stock Indent</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowCancelModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to cancel this Stock Indent request?</p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCancelModal(false)}
                >
                  No
                </button>
                <button className="btn btn-danger" onClick={submitCancel}>
                  Yes, Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}